import { v3 } from './babylonjs/utils';
import { CameraConfig, Model, ModelConfig } from './types';

export const MAX_WORD_SUGGESTION = 60;

export const OPENAI_TIMEOUT_MILLISECONDS = 5_000;
const REACT_AI_API_URL = process.env.REACT_AI_AGENT_API_URL;
export const CHAT_MESSAGES_URL = `${REACT_AI_API_URL}/speech/synthesize_and_tts/`;
export const TTS_URL = `${REACT_AI_API_URL}/speech/tts_openai/`;

export const MAX_CHARS = 300;
export const CORNER_ICON_SIZE = 24;

export const MAIN_CAMERA_NAME = 'MainCamera';

export const DEFAULT_INITIAL_MESSAGE = "Hey, how's it going?";
export const DEFAULT_PROMPT = `Prompt - You are an AI language model,
and you will be chatting as a fun, upbeat, and friendly character. 
Make sure not to mention your role as an AI or the character you are portraying. 
Keep your responses concise, no longer than ${MAX_WORD_SUGGESTION} words per response. 
Engage in a lively and positive conversation with the user.`;
export const DEFAULT_SPEECH_RECOGNITION_LANGUAGE_CODE = 'en-US';

export const DEFAULT_VOICE = 'ash';
export const DEFAULT_MODEL: Model = 'vroid_girl1';

export const defaultCameraConfig: CameraConfig = {
    alpha: Math.PI / 2,
    beta: Math.PI / 2.5,
    radius: 1.5,
    target: v3(0, 0.7, 0),
};

const defaultConfig: ModelConfig = {
    cameraConfig: defaultCameraConfig,
    // voice: "en-US-Neural2-H",
    voice: DEFAULT_VOICE,
    initialAnimation: 'idle3_hand_hips',
    faceMeshName: 'Face',
    morphTargets: {
        mouthMovement: 'Face.M_F00_000_00_Fcl_MTH_A',
        leftEyeClose: 'Face.M_F00_000_00_Fcl_EYE_Close_L',
        rightEyeClose: 'Face.M_F00_000_00_Fcl_EYE_Close_R',
    },
    idleAnimations: ['idle1', 'idle2', 'idle3_hand_hips'],
    talkingBodyAnimations: ['talking1', 'talking2_head_shake', 'talking3'],
    positionOffset: v3(0, 0.015, 0.2),
};

// Enhanced config with multiple mouth movements and eye actions
const enhancedConfig: ModelConfig = {
    cameraConfig: defaultCameraConfig,
    voice: DEFAULT_VOICE,
    initialAnimation: '',
    faceMeshName: 'Face',
    morphTargets: {
        // Legacy support
        mouthMovement: 'Face.M_F00_000_00_Fcl_MTH_A',
        // Multiple mouth movements for more natural speech
        mouthMovements: [
            {
                name: 'neutral_talk',
                morphTargets: [
                    { name: 'M_F00_000_00_Fcl_MTH_A', weight: 0.8, duration: 0.3 },
                    { name: 'M_F00_000_00_Fcl_MTH_O', weight: 0.2, duration: 0.2 },
                ],
                probability: 0.4,
            },
            {
                name: 'emphasized_talk',
                morphTargets: [
                    { name: 'M_F00_000_00_Fcl_MTH_A', weight: 1.0, duration: 0.4 },
                    { name: 'M_F00_000_00_Fcl_MTH_O', weight: 0.3, duration: 0.3 },
                ],
                probability: 0.3,
            },
            {
                name: 'soft_talk',
                morphTargets: [
                    { name: 'M_F00_000_00_Fcl_MTH_A', weight: 0.5, duration: 0.2 },
                    { name: 'M_F00_000_00_Fcl_MTH_E', weight: 0.3, duration: 0.2 },
                ],
                probability: 0.3,
            },
        ],
        // Eye actions for more natural expressions
        eyeActions: [
            {
                name: 'thinking',
                morphTargets: [
                    { name: 'M_F00_000_00_Fcl_EYE_Close_L', weight: 0.3, duration: 0.5 },
                    { name: 'M_F00_000_00_Fcl_EYE_Close_R', weight: 0.3, duration: 0.5 },
                ],
                probability: 0.2,
                cooldown: 3.0,
            },
            {
                name: 'focused',
                morphTargets: [
                    { name: 'M_F00_000_00_Fcl_EYE_Open_L', weight: 0.8, duration: 1.0 },
                    { name: 'M_F00_000_00_Fcl_EYE_Open_R', weight: 0.8, duration: 1.0 },
                ],
                probability: 0.3,
                cooldown: 2.0,
            },
            {
                name: 'gentle_blink',
                morphTargets: [
                    { name: 'M_F00_000_00_Fcl_EYE_Close_L', weight: 0.6, duration: 0.2 },
                    { name: 'M_F00_000_00_Fcl_EYE_Close_R', weight: 0.6, duration: 0.2 },
                ],
                probability: 0.5,
                cooldown: 1.5,
            },
        ],
        leftEyeClose: 'M_F00_000_00_Fcl_EYE_Close_L',
        rightEyeClose: 'M_F00_000_00_Fcl_EYE_Close_R',
    },
    idleAnimations: [
        // Support both string format (default speed 1.0) and object format with custom speed
        'idle1',
        { name: 'idle2', playbackSpeed: 0.7 }, // Slower idle animation
        { name: 'idle3_hand_hips', playbackSpeed: 0.8 }, // Slightly slower
    ],
    talkingBodyAnimations: [
        // Support both string format (default speed 1.0) and object format with custom speed
        'talking1',
        { name: 'talking2_head_shake', playbackSpeed: 1.0 }, // Normal speed
        { name: 'talking3', playbackSpeed: 0.9 }, // Slightly slower
    ],
    positionOffset: v3(0, 0.015, 0.2),
};

export const models = {
    vroid_girl1: {
        ...defaultConfig,
        name: 'VRoid Girl 1',
    },
    man_ai_teacher: {
        ...enhancedConfig,
        morphTargets: {
            // Legacy support
            mouthMovement: 'mouthOpen',
            mouthMovements: [
                {
                    name: 'teaching_explanation',
                    morphTargets: [
                        { name: 'mouthOpen', weight: 0.9, duration: 0.4 },
                        { name: 'mouthPucker', weight: 0.2, duration: 0.2 },
                    ],
                    probability: 0.4,
                },
                {
                    name: 'questioning',
                    morphTargets: [
                        { name: 'mouthOpen', weight: 0.5, duration: 0.3 },
                        { name: 'mouthFrownLeft', weight: 0.25, duration: 0.3 },
                        { name: 'mouthFrownRight', weight: 0.25, duration: 0.3 },
                    ],
                    probability: 0.3,
                },
                {
                    name: 'encouraging',
                    morphTargets: [
                        { name: 'mouthOpen', weight: 0.6, duration: 0.3 },
                        { name: 'mouthSmile', weight: 0.4, duration: 0.4 },
                    ],
                    probability: 0.3,
                },
            ],
            eyeActions: [
                {
                    name: 'attentive',
                    morphTargets: [
                        { name: 'eyeWideLeft', weight: 0.8, duration: 0.6 },
                        { name: 'eyeWideRight', weight: 0.8, duration: 0.6 },
                        { name: 'eyesClosed', weight: 0.3, duration: 0.6 },
                    ],
                    probability: 0.5,
                },
                {
                    name: 'friendly',
                    morphTargets: [
                        { name: 'eyeWideLeft', weight: 0.8, duration: 0.6 },
                        { name: 'eyeWideRight', weight: 0.8, duration: 0.6 },
                    ],
                    probability: 0.5,
                },
            ],
            leftEyeClose: 'eyesClosed',
            rightEyeClose: 'eyesClosed',
        },
        faceMeshName: 'Head_Mesh',
        cameraConfig: {
            alpha: Math.PI / 2.3,
            beta: Math.PI / 2,
            radius: 3.8,
            target: v3(0, 0.9, 0),
        },
        positionOffset: v3(0, 0.03, 0),
        idleAnimations: [{ name: 'Jame_Teacher_Model_(2)', playbackSpeed: 0.1 }],
        talkingBodyAnimations: [{ name: 'Jame_Teacher_Model_(2)', playbackSpeed: 1.0 }],
        name: 'Man AI Teacher',
    },
} as const;
